import React, { useCallback, useState } from 'react';
import { convertArrayToCSV } from 'convert-array-to-csv';
import FileSaver from 'file-saver';
import capitalize from 'lodash/capitalize';
import { format } from 'date-fns';
import moment from 'moment';
import { Appearance } from '@atlaskit/button';
import { CommerceAddress } from '@atlassian/cssxp-types-opportunity';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { useTransactionAccountContext } from '@css-xp/base/common-context';
import { getProductUsageDetails } from '@css-xp/base/common-tabs';
import { getNextBillDateText } from '@css-xp/ppc-common';
import { formatAddressLabel, useFeatureToggle } from '@css-xp/base';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { useLoadEntitlementList } from '@css-xp/ppc-custom-hooks';
import { fetchEntitlementListForDownloadCSV } from '../queries/fetch-entitlement-list-for-download-csv';
import {
  defaultSortOptions,
  DownloadCSVButton,
  DownloadCSVCommonProps,
  DownloadCSVUsingGetAggregateCcpList,
  headers,
  useVariables,
} from './download-csv-common';

const getCustomerContactsDetails = (product: generatedTypesNew.TransactionAccountProduct | null) => {
  const eccData: Array<string> = [];
  product?.endCustomerContacts?.forEach((customerContact) => {
    eccData.push(customerContact.name);
  });
  return eccData.join(', ');
};

type ProductType = generatedTypesNew.TransactionAccountProduct | null;
export const getTransformedProductDetails = (
  product: ProductType,
  transactionAccount: generatedTypesNew.TransactionAccount
): string[] => {
  const invoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes?.find(
    (invoiceGroup) => invoiceGroup?.id === product?.billingGroupId
  );
  const createdDate = moment.utc(product?.createdDate).format(NEW_DATE_FORMAT);

  return [
    // col 1: Subscription Id
    product?.id ?? '-',
    // col 2: Subscription base product name
    product?.baseProduct ?? '-',
    // col 3: edition : Premium, Standard, Free
    product?.edition ?? '-',
    // col 4: entitlement Number (Slug) info
    product?.entitlementNumber ?? '-',
    // col 5: Organization name
    product?.orgName ?? '-',
    // col 6: cloud site name
    product?.cloudSiteHostname ?? '-',
    // col 7: product usage details in users / tiers / units
    getProductUsageDetails(product as generatedTypesNew.InvoiceGroupProduct | null),
    // col 8: Next bill / Expiry date
    getNextBillDateText(product) ?? '-',
    // col 9: Billing cycle
    product?.billingFrequency ? capitalize(product.billingFrequency) : '-',
    // col 10: billing Status: Active / Deactivated
    product?.billingStatus ? capitalize(product?.billingStatus) : '-',
    // col 11: linked apps
    product?.linkedAppsId?.length.toString() ?? '-',
    // col 12: customer contacts
    getCustomerContactsDetails(product),
    // col 13: Company
    invoiceGroup?.shipToParty?.name ?? '-',
    // col 14: Sold-to address
    formatAddressLabel(invoiceGroup?.shipToParty as CommerceAddress) || '-',
    // col 15: Created
    createdDate ?? '-',
  ];
};

const DownloadCSVUsingLoadEntitlementList = ({ variables, loading = false, ...props }: DownloadCSVCommonProps) => {
  const { loadEntitlementList } = useLoadEntitlementList(fetchEntitlementListForDownloadCSV, variables);
  return <DownloadCSVButton {...{ loadEntitlementList, buttonLoading: loading, ...props }} />;
};

interface props {
  transactionAccountId: string;
  buttonAppearance?: Appearance;
  buttonText?: string;
  className?: string;
  filterOptions?: generatedTypesNew.FilterOption[];
  isDarkTheme?: boolean;
  sortOptions?: generatedTypesNew.Maybe<generatedTypesNew.SortType>;
}

export const DownloadCSV = ({
  transactionAccountId,
  buttonAppearance,
  buttonText,
  className,
  filterOptions = [],
  isDarkTheme = false,
  sortOptions = defaultSortOptions,
}: props) => {
  const { data: transactionAccountData, getTransactionAccount } = useTransactionAccountContext();
  const transactionAccount = getTransactionAccount(transactionAccountData);
  const [loading, setLoading] = useState(false);
  const [isEnabled] = useFeatureToggle();
  const isUseGetAggregateCcpListHookEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_USE_GET_AGGREGATE_CCP_LIST_HOOK);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );

  const variables = useVariables({
    transactionAccountId,
    filterOptions,
    sortOptions,
    isSandboxSupportForSubscriptionsEnabled,
  });
  const handleDownloadClick = useCallback(
    async (loadEntitlementList) => {
      setLoading(true);
      const entitlementList = await loadEntitlementList();
      setLoading(false);
      const results = entitlementList ?? [];
      const formattedSubscriptionData: string[][] = [headers];
      if (results?.length > 0) {
        results.forEach((item: ProductType) => {
          formattedSubscriptionData.push(
            getTransformedProductDetails(item, transactionAccount as generatedTypesNew.TransactionAccount)
          );
        });
      }
      const csv = convertArrayToCSV(formattedSubscriptionData);
      const csvFile = new Blob([csv], { type: 'text/csv' });
      const fileName = `subscriptions-${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.csv`;
      FileSaver.saveAs(csvFile, fileName);
    },
    [transactionAccount]
  );

  const commonProps: DownloadCSVCommonProps = {
    transactionAccountId,
    variables,
    buttonAppearance,
    buttonText,
    className,
    isDarkTheme,
    handleDownloadClick,
    loading,
  };

  return isUseGetAggregateCcpListHookEnabled ? (
    <DownloadCSVUsingGetAggregateCcpList {...commonProps} />
  ) : (
    <DownloadCSVUsingLoadEntitlementList {...commonProps} />
  );
};
